import { APP_ROUTES } from './paths';

import type { Brand, BrandFeatureFlag } from '@/shared/types/brand';
import type { FeatureKey } from '@/shared/types/features';

export interface NavPage {
  /** Doubles as the sidebar label and the analytics `page_name`. */
  title: string;
  path: string;
  /** Primary feature — the analytics `feature_key` and the sidebar icon key. */
  featureKey: FeatureKey;
  /**
   * Any of these grants page access (OR-gated); defaults to `[featureKey]`.
   * Analytics and icon still key off the single primary `featureKey`.
   */
  accessFeatureKeys?: readonly FeatureKey[];
  /**
   * Per-brand flag on the brand DTO that also gates this page. `user.features`
   * is an OR across the org's brands, so without this a page enabled for one
   * brand stays visible — and 403s — while another brand is selected.
   */
  brandFlag?: BrandFeatureFlag;
  /**
   * Sidebar grouping (S3). Required — a page with no group would silently fall
   * out of the rail, and `as const satisfies` below turns that into a compile
   * error rather than a missing nav item someone notices in production.
   */
  group: NavGroup;
}

/**
 * The per-brand half of a page gate. `?? true` so a backend that has not
 * populated the flag yet never hides a page a partner already has; a page
 * without a `brandFlag` is brand-agnostic and always passes.
 */
export const isPageEnabledForBrand = (
  page: Pick<NavPage, 'brandFlag'>,
  brand: Brand | undefined
): boolean => (page.brandFlag ? (brand?.[page.brandFlag] ?? true) : true);

/** The four sidebar groups, in render order. */
export const NAV_GROUPS = [
  'Programs',
  'Operations',
  'Reporting',
  'Resources'
] as const;

export type NavGroup = (typeof NAV_GROUPS)[number];

// Order matches the partner-facing navigation flow: program discovery → setup
// (bag ordering) → operating tools (analytics, cashout tile, calculator) →
// reference materials (reports, knowledge base, gift cards) → optional add-ons
// (dropshipping).
//
// Kept deliberately icon-free. The analytics page resolver reads this list, and
// it is reachable from the prerendered public pages — pulling lucide-react in
// here would drag the whole icon set into the landing chunk and the SSR bundle.
// Layout/constants.ts decorates these with icons for the sidebar.
export const NAV_PAGES = [
  {
    title: 'Service Offerings',
    path: APP_ROUTES.PROGRAMS,
    featureKey: 'PROGRAMS',
    group: 'Programs'
  },
  {
    title: 'Physical Clean Out Kit Order',
    path: APP_ROUTES.KITS_ORDERING,
    featureKey: 'KITS_ORDERING',
    group: 'Operations'
  },
  {
    title: 'Analytics',
    path: APP_ROUTES.ANALYTICS,
    featureKey: 'ANALYTICS',
    brandFlag: 'analytics',
    group: 'Reporting'
  },
  {
    title: 'Brand Performance on ThredUp',
    path: APP_ROUTES.BRAND_PERFORMANCE_THREDUP,
    featureKey: 'BRAND_PERFORMANCE_THREDUP',
    group: 'Reporting'
  },
  {
    title: 'Cashout Tile',
    path: APP_ROUTES.CASHOUT_TILE,
    featureKey: 'CASHOUT_TILE',
    group: 'Programs'
  },
  {
    title: 'ROI & Sustainability Calculator',
    path: APP_ROUTES.ROI_CALCULATOR,
    featureKey: 'ROI_CALCULATOR',
    group: 'Reporting'
  },
  {
    title: 'Reports',
    path: APP_ROUTES.REPORTS,
    featureKey: 'REPORTS',
    accessFeatureKeys: ['REPORTS', 'BULK_CONSIGNMENT_REPORTS'],
    group: 'Reporting'
  },
  {
    title: 'Knowledge Base',
    path: APP_ROUTES.KNOWLEDGE_BASE,
    featureKey: 'KNOWLEDGE_BASE',
    group: 'Resources'
  },
  {
    title: 'Gift Card Automation',
    path: APP_ROUTES.GIFT_CARDS,
    featureKey: 'GIFT_CARDS',
    group: 'Programs'
  },
  {
    title: 'Dropshipping',
    path: APP_ROUTES.DROPSHIPPING,
    featureKey: 'DROPSHIPPING',
    // Same brand-scoped gate as Analytics: `/brands/:brandId/dropshipping` is
    // guarded per brand, so the org-level OR would otherwise show this for a
    // brand that has it off and 403 on open.
    brandFlag: 'dropshipping',
    group: 'Operations'
  }
] as const satisfies readonly NavPage[];

/**
 * The feature keys that actually appear in the navigation — a strict subset of
 * `FeatureKey`. Lets the sidebar's icon map be exhaustively checked at compile
 * time, so adding a page here without an icon is a type error rather than a
 * missing icon at runtime.
 */
export type NavFeatureKey = (typeof NAV_PAGES)[number]['featureKey'];
